import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { getFeedUseCase, deletePostUseCase, likePostUseCase, unlikePostUseCase } from '@/src/core/dependencies';
import { PostWithDetails } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { FeedScreen } from '@/src/presentation/screens/FeedScreen';
import { useFocusEffect } from '@react-navigation/native';
import { useRouter } from 'expo-router';
import { useCallback, useState } from 'react';
import { Platform, Share } from 'react-native';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { logger } from '@/src/core/utils';

const PAGE_SIZE = 20;

export default function FeedContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [posts, setPosts] = useState<PostWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  useFocusEffect(
    useCallback(() => {
      loadFeed();
    }, [])
  );

  const loadFeed = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setPage(0);
      setHasMore(true);
      const data = await getFeedUseCase.execute(user.id, PAGE_SIZE, 0);
      setPosts(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      logger.error('Feed', 'Failed to load feed', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadMorePosts = async () => {
    if (loadingMore || !hasMore || !user) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const data = await getFeedUseCase.execute(user.id, PAGE_SIZE, nextPage * PAGE_SIZE);

      if (data.length > 0) {
        setPosts((prev) => [...prev, ...data]);
        setPage(nextPage);
        setHasMore(data.length === PAGE_SIZE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      logger.error('Feed', 'Failed to load more posts', { error });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!user) return;

    platformAlert(t('feed.deletePost'), t('feed.deletePostConfirm'), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('common.delete'),
        style: 'destructive',
        onPress: async () => {
          try {
            await deletePostUseCase.execute(postId, user.id);
            setPosts((prev) => prev.filter((p) => p.id !== postId));
            platformAlert(t('common.success'), t('alerts.postDeleted'));
          } catch (error) {
            logger.error('Feed', 'Failed to delete post', { error });
            platformAlert(t('common.error'), t('feed.failedToPost'));
          }
        },
      },
    ]);
  };

  const handleUserPress = (userId: string) => {
    router.push(`/profile/${userId}`);
  };

  const handleTournamentPress = (tournamentId: string) => {
    router.push(`/tournament/${tournamentId}`);
  };

  const handleSharePost = async (postId: string) => {
    const url = `https://tournaly.app/post/${postId}`;
    const title = t('feed.sharePost');
    if (Platform.OS === 'web') {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ url, title }).catch(() => {});
      } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(url).catch(() => {});
        platformAlert(t('common.success'), t('feed.linkCopied'));
      }
    } else {
      await Share.share({ message: url, title }).catch(() => {});
    }
  };

  const handleLikePress = async (postId: string, isLiked: boolean) => {
    if (!user) return;

    // Optimistic update
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? {
              ...p,
              isLikedByUser: !isLiked,
              likeCount: isLiked ? p.likeCount - 1 : p.likeCount + 1,
            }
          : p
      )
    );

    try {
      if (isLiked) {
        await unlikePostUseCase.execute(postId, user.id);
      } else {
        await likePostUseCase.execute(postId, user.id);
      }
    } catch (error) {
      // Revert optimistic update on error
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? {
                ...p,
                isLikedByUser: isLiked,
                likeCount: isLiked ? p.likeCount + 1 : p.likeCount - 1,
              }
            : p
        )
      );
      logger.error('Feed', 'Failed to toggle like', { error });
    }
  };

  return (
    <FeedScreen
      posts={posts}
      loading={loading}
      loadingMore={loadingMore}
      currentUserId={user?.id}
      onRefresh={loadFeed}
      onLoadMore={loadMorePosts}
      onUserPress={handleUserPress}
      onTournamentPress={handleTournamentPress}
      onDeletePost={handleDeletePost}
      onLikePress={handleLikePress}
      onSharePost={handleSharePost}
      colors={colors}
    />
  );
}
